import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete } from '@material-ui/lab';
import Grid from '@material-ui/core/Grid';

import { Button, TextInput } from '@Components/common';
import { msgFieldRequired } from '@Helpers/errorMessages';
import { VALIDATE_POSTCODE_REGEX } from '@Helpers/fieldValidation';
import style from './AddressSection.module.scss';
import { RiAddFill } from 'react-icons/ri';
import { LocationFields } from '@Types';
import classnames from 'classnames';

const autoCompleteGetOption = (option: string) => option;

interface AddressSectionProps {
  countyRequired?: boolean;
  isLoading: boolean;
  header?: string;
  required?: boolean;
}

export const AddressSection: React.FC<AddressSectionProps> = ({
  countyRequired,
  isLoading,
  header,
  required,
}) => {
  const { t } = useTranslation('common');
  const { register, control, errors: fieldErrors, watch } = useFormContext();

  const [showAdditionalAddressLines, setShowAdditionalAddressLines] = useState(false);

  const fieldValue = useCallback(
    (fieldName: keyof LocationFields) => {
      const value = watch(fieldName);
      return value || '';
    },
    [watch],
  );

  const hasAdditionalAddressLines = useMemo(() => {
    return !!fieldValue('address2') || !!fieldValue('address3');
  }, [fieldValue]);

  useEffect(() => {
    // Show fields if there are values OR if they were previously shown
    if (hasAdditionalAddressLines || showAdditionalAddressLines) {
      setShowAdditionalAddressLines(true);
    }
  }, [hasAdditionalAddressLines, showAdditionalAddressLines]);

  return (
    <>
      {header && <h4>{header}</h4>}
      <TextInput
        label={t('address_line', { num: 1 })}
        type="text"
        inputRef={register(required ? { required: msgFieldRequired } : {})}
        name="address1"
        error={!!fieldErrors.address1}
        helperText={fieldErrors.address1?.message}
        disabled={isLoading}
        InputLabelProps={{ shrink: fieldValue('address1') }}
      />
      {!showAdditionalAddressLines && (
        <Button
          size="small"
          buttonType="text"
          onClick={() => {
            setShowAdditionalAddressLines(true);
          }}
          startIcon={<RiAddFill />}
          className={style.addAddressLineButton}
        >
          {t('add_address_line')}
        </Button>
      )}

      {/* Ensure the additional address lines are always in the DOM but hidden if not populated on page load.
      They can then be shown in response to the user clicking the "Use this address" button if either of those fields are populated. */}
      <div
        className={classnames(
          showAdditionalAddressLines
            ? style.additionalAddressLines
            : style.hideAdditionalAddressLines,
        )}
      >
        <TextInput
          label={t('address_line', { num: 2, context: 'optional' })}
          type="text"
          name="address2"
          inputRef={register({})}
          error={!!fieldErrors.address2}
          helperText={fieldErrors.address2?.message}
          disabled={isLoading}
          InputLabelProps={{ shrink: fieldValue('address2') }}
        />
        <TextInput
          label={t('address_line', { num: 3, context: 'optional' })}
          type="text"
          name="address3"
          inputRef={register({})}
          error={!!fieldErrors.address3}
          helperText={fieldErrors.address3?.message}
          disabled={isLoading}
          InputLabelProps={{ shrink: fieldValue('address3') }}
        />
      </div>
      <div className={style.addressBar}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TextInput
              label={t('city')}
              inputRef={register(required ? { required: msgFieldRequired } : {})}
              name="city"
              error={!!fieldErrors.city}
              helperText={fieldErrors.city?.message}
              disabled={isLoading}
              InputLabelProps={{ shrink: fieldValue('city') }}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="county"
              rules={countyRequired ? { required: msgFieldRequired } : {}}
              control={control}
              defaultValue={''}
              render={({ ref, onChange, value }) => (
                <Autocomplete
                  value={value || ''}
                  onChange={(_, val) => onChange(val || '')}
                  options={t('counties:counties', { returnObjects: true }) as string[]}
                  getOptionLabel={autoCompleteGetOption}
                  disabled={isLoading}
                  renderInput={(params) => (
                    <TextInput
                      label={t('county')}
                      type="text"
                      error={!!fieldErrors.county}
                      helperText={fieldErrors.county?.message}
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />
          </Grid>
        </Grid>
      </div>

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <TextInput
            label={t('postcode')}
            inputRef={register(
              required
                ? {
                    required: msgFieldRequired,
                    pattern: {
                      value: VALIDATE_POSTCODE_REGEX,
                      message: t('invalid_postcode'),
                    },
                  }
                : {
                    pattern: {
                      value: VALIDATE_POSTCODE_REGEX,
                      message: t('invalid_postcode'),
                    },
                  },
            )}
            name="postcode"
            error={!!fieldErrors.postcode}
            helperText={fieldErrors.postcode?.message}
            InputLabelProps={{ shrink: fieldValue('postcode') }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            label={t('country')}
            name="country"
            value={t('countries.uk')}
            disabled
            type="text"
          />
        </Grid>
      </Grid>
    </>
  );
};
